import { sumPlatformQueryCredits } from '@lensora/shared'
import { Button, Spinner } from '@lensora/ui'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Search,
  ChevronDown,
  ChevronRight,
  Play,
  CheckCircle2,
  XCircle,
  Zap,
  AlertTriangle,
  Clock,
  Loader2,
  History,
} from 'lucide-react'
import * as React from 'react'

import { useActiveWebsite } from '../../../hooks/use-active-website'
import { api, ApiError } from '../../../lib/api'

// ─── Types ────────────────────────────────────────────────────────────────────

type QueryType = 'discovery' | 'comparison' | 'recommendation' | 'how_to' | 'definition'

interface Prompt {
  id: string
  text: string
  queryType: QueryType
  industry: string
}

type Sentiment = 'positive' | 'neutral' | 'negative' | 'not_mentioned'

interface SimResult {
  id: string
  prompt: string
  platform: string
  mentioned: boolean
  position: number | null
  sentiment: Sentiment | null
  fullResponse: string
  competitorMentions: string[]
  visibilityVerdict?: string
  improvementAction?: string
  error?: string
}

interface HistoryResult {
  id: string
  query_text: string
  platform: string
  brand_mentioned: boolean
  sentiment: string | null
  competitor_mentions: string[]
  response_text: string
  created_at: string
}

interface SimulateResponse {
  results: Array<{
    platform: string
    raw_response: string
    brand_mentioned: boolean
    mention_position: number | null
    sentiment: string
    competitor_mentions?: string[]
    visibility_verdict?: string
    improvement_action?: string
    error?: string
  }>
  new_balance?: number
  was_rewritten?: boolean
  original_prompt?: string
  rewritten_prompt?: string
}

// ─── Static data ──────────────────────────────────────────────────────────────

const PROMPTS: Prompt[] = [
  { id: 'saas1', text: 'What is the best CRM software for small businesses?', queryType: 'recommendation', industry: 'SaaS/Software' },
  { id: 'saas2', text: 'Compare Salesforce vs HubSpot vs Pipedrive for enterprise', queryType: 'comparison', industry: 'SaaS/Software' },
  { id: 'saas3', text: 'What project management tool should a startup use?', queryType: 'recommendation', industry: 'SaaS/Software' },
  { id: 'saas4', text: 'How do I choose the right SaaS tool for my business?', queryType: 'how_to', industry: 'SaaS/Software' },
  { id: 'saas5', text: 'What is cloud-based software?', queryType: 'definition', industry: 'SaaS/Software' },
  { id: 'cyber1', text: 'What is the best endpoint security solution for enterprises?', queryType: 'recommendation', industry: 'Cybersecurity' },
  { id: 'cyber2', text: 'Compare CrowdStrike vs SentinelOne vs Microsoft Defender', queryType: 'comparison', industry: 'Cybersecurity' },
  { id: 'cyber3', text: 'How do I protect my business from ransomware attacks?', queryType: 'how_to', industry: 'Cybersecurity' },
  { id: 'cyber4', text: 'What is a zero-trust security model?', queryType: 'definition', industry: 'Cybersecurity' },
  { id: 'cyber5', text: 'Find me a penetration testing service for my company', queryType: 'discovery', industry: 'Cybersecurity' },
  { id: 'mkt1', text: 'What are the best marketing automation platforms?', queryType: 'recommendation', industry: 'Digital Marketing' },
  { id: 'mkt2', text: 'Compare Mailchimp vs Klaviyo vs ActiveCampaign', queryType: 'comparison', industry: 'Digital Marketing' },
  { id: 'mkt3', text: 'How do I improve my website SEO ranking?', queryType: 'how_to', industry: 'Digital Marketing' },
  { id: 'mkt4', text: 'What is performance marketing?', queryType: 'definition', industry: 'Digital Marketing' },
  { id: 'mkt5', text: 'Find me the best social media management tools', queryType: 'discovery', industry: 'Digital Marketing' },
  { id: 'ecom1', text: 'What is the best e-commerce platform for small businesses?', queryType: 'recommendation', industry: 'E-commerce' },
  { id: 'ecom2', text: 'Compare Shopify vs WooCommerce vs BigCommerce', queryType: 'comparison', industry: 'E-commerce' },
  { id: 'ecom3', text: 'How do I reduce cart abandonment on my online store?', queryType: 'how_to', industry: 'E-commerce' },
  { id: 'ecom4', text: 'What is headless commerce?', queryType: 'definition', industry: 'E-commerce' },
  { id: 'ecom5', text: 'Find me a payment gateway for international sales', queryType: 'discovery', industry: 'E-commerce' },
  { id: 'legal1', text: 'What is the best legal practice management software?', queryType: 'recommendation', industry: 'Legal Services' },
  { id: 'legal2', text: 'Compare Clio vs MyCase vs PracticePanther for law firms', queryType: 'comparison', industry: 'Legal Services' },
  { id: 'legal3', text: 'How do I choose a business law firm for my startup?', queryType: 'how_to', industry: 'Legal Services' },
  { id: 'legal4', text: 'What is alternative dispute resolution?', queryType: 'definition', industry: 'Legal Services' },
  { id: 'legal5', text: 'Find me an IP attorney for trademark registration', queryType: 'discovery', industry: 'Legal Services' },
  { id: 'fin1', text: 'What is the best accounting software for small businesses?', queryType: 'recommendation', industry: 'Accounting/Finance' },
  { id: 'fin2', text: 'Compare QuickBooks vs Xero vs FreshBooks', queryType: 'comparison', industry: 'Accounting/Finance' },
  { id: 'fin3', text: 'How do I manage cash flow for my small business?', queryType: 'how_to', industry: 'Accounting/Finance' },
  { id: 'fin4', text: 'What is cash flow forecasting?', queryType: 'definition', industry: 'Accounting/Finance' },
  { id: 'fin5', text: 'Find me a financial advisor for retirement planning', queryType: 'discovery', industry: 'Accounting/Finance' },
  { id: 'health1', text: 'What is the best EHR software for medical practices?', queryType: 'recommendation', industry: 'Healthcare' },
  { id: 'health2', text: 'Compare Epic vs Cerner vs Athenahealth for hospitals', queryType: 'comparison', industry: 'Healthcare' },
  { id: 'health3', text: 'How do I choose a telemedicine platform for my clinic?', queryType: 'how_to', industry: 'Healthcare' },
  { id: 'health4', text: 'What is value-based healthcare?', queryType: 'definition', industry: 'Healthcare' },
  { id: 'health5', text: 'Find me a medical billing service for my practice', queryType: 'discovery', industry: 'Healthcare' },
  { id: 're1', text: 'What is the best CRM for real estate agents?', queryType: 'recommendation', industry: 'Real Estate' },
  { id: 're2', text: 'Compare Follow Up Boss vs LionDesk vs kvCORE', queryType: 'comparison', industry: 'Real Estate' },
  { id: 're3', text: 'How do I generate leads as a real estate agent?', queryType: 'how_to', industry: 'Real Estate' },
  { id: 're4', text: "What is a buyer's agent?", queryType: 'definition', industry: 'Real Estate' },
  { id: 're5', text: 'Find me property management software for landlords', queryType: 'discovery', industry: 'Real Estate' },
  { id: 'hr1', text: 'What is the best applicant tracking system for startups?', queryType: 'recommendation', industry: 'Recruitment/HR' },
  { id: 'hr2', text: 'Compare Workday vs BambooHR vs Rippling for HR', queryType: 'comparison', industry: 'Recruitment/HR' },
  { id: 'hr3', text: 'How do I improve employee retention at my company?', queryType: 'how_to', industry: 'Recruitment/HR' },
  { id: 'hr4', text: 'What is employer branding?', queryType: 'definition', industry: 'Recruitment/HR' },
  { id: 'hr5', text: 'Find me a recruitment agency for tech roles', queryType: 'discovery', industry: 'Recruitment/HR' },
  { id: 'hosp1', text: 'What is the best hotel management software?', queryType: 'recommendation', industry: 'Hospitality/Travel' },
  { id: 'hosp2', text: 'Compare Cloudbeds vs Opera PMS vs Mews', queryType: 'comparison', industry: 'Hospitality/Travel' },
  { id: 'hosp3', text: 'How do I increase direct bookings for my hotel?', queryType: 'how_to', industry: 'Hospitality/Travel' },
  { id: 'hosp4', text: 'What is revenue management in hospitality?', queryType: 'definition', industry: 'Hospitality/Travel' },
  { id: 'hosp5', text: 'Find me a channel manager for my bed and breakfast', queryType: 'discovery', industry: 'Hospitality/Travel' },
  { id: 'ind1', text: 'What are the best construction equipment manufacturers in Europe?', queryType: 'recommendation', industry: 'Industrial & Manufacturing' },
  { id: 'ind2', text: 'Compare leading excavator and heavy machinery brands', queryType: 'comparison', industry: 'Industrial & Manufacturing' },
  { id: 'ind3', text: 'What is the most reliable agricultural equipment brand?', queryType: 'recommendation', industry: 'Industrial & Manufacturing' },
  { id: 'ind4', text: 'Best material handling and logistics equipment suppliers', queryType: 'discovery', industry: 'Industrial & Manufacturing' },
  { id: 'ind5', text: 'How do I choose the right industrial machinery supplier for my business?', queryType: 'how_to', industry: 'Industrial & Manufacturing' },
]

const INDUSTRIES = [
  'All', 'SaaS/Software', 'Cybersecurity', 'Digital Marketing', 'E-commerce',
  'Legal Services', 'Accounting/Finance', 'Healthcare', 'Real Estate',
  'Recruitment/HR', 'Hospitality/Travel', 'Industrial & Manufacturing',
]

const PLATFORM_MAP: Record<string, string> = {
  'Groq (Llama 3.3)': 'groq',
  ChatGPT: 'chatgpt',
  Claude: 'claude',
  Gemini: 'gemini',
  Perplexity: 'perplexity',
  'Bing Copilot': 'bing',
  'Google AI Overview': 'google-ai',
  'ChatGPT Search': 'chatgpt-search',
  'Claude Search': 'claude-search',
}

const PLATFORMS = Object.keys(PLATFORM_MAP)

const QUERY_TYPE_LABELS: Record<QueryType, string> = {
  discovery: 'Discovery',
  comparison: 'Comparison',
  recommendation: 'Recommendation',
  how_to: 'How-To',
  definition: 'Definition',
}

const VERDICT_CONFIG = {
  STRONG:      { colorClass: 'text-feedback-success', bgClass: 'bg-feedback-success/10', dotClass: 'bg-feedback-success' },
  MODERATE:    { colorClass: 'text-feedback-info',    bgClass: 'bg-feedback-info/10',    dotClass: 'bg-feedback-info' },
  WEAK:        { colorClass: 'text-feedback-warning', bgClass: 'bg-feedback-warning/10', dotClass: 'bg-feedback-warning' },
  NOT_PRESENT: { colorClass: 'text-feedback-error',   bgClass: 'bg-feedback-error/10',   dotClass: 'bg-feedback-error' },
} as const

const SENTIMENT_COLORS: Record<string, string> = {
  positive: 'bg-feedback-success/10 text-feedback-success border-feedback-success/30',
  neutral:  'bg-surface-overlay text-text-muted border-border',
  negative: 'bg-feedback-error/10 text-feedback-error border-feedback-error/30',
}

// ─── Components ───────────────────────────────────────────────────────────────

function SentimentChip({ sentiment }: { sentiment: Sentiment | null }) {
  if (!sentiment || sentiment === 'not_mentioned') return null
  return (
    <span className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold capitalize ${SENTIMENT_COLORS[sentiment] ?? ''}`}>
      {sentiment}
    </span>
  )
}

function MentionedBadge({ mentioned }: { mentioned: boolean }) {
  return mentioned ? (
    <span className="flex items-center gap-1 text-xs font-semibold text-feedback-success">
      <CheckCircle2 className="h-4 w-4" /> Yes
    </span>
  ) : (
    <span className="flex items-center gap-1 text-xs font-semibold text-brand-accent">
      <XCircle className="h-4 w-4" /> No
    </span>
  )
}

function VisibilityVerdictCard({ verdict, position, competitors, action }: {
  verdict: string
  position: number | null
  competitors: string[]
  action: string
}) {
  const cfg = VERDICT_CONFIG[verdict as keyof typeof VERDICT_CONFIG] ?? VERDICT_CONFIG.NOT_PRESENT
  return (
    <div className="mt-3 rounded-xl border-l-4 border-brand-accent bg-surface-raised p-4">
      <p className="mb-3 text-[10px] font-bold uppercase tracking-widest text-text-muted">Visibility Analysis</p>
      <div className="mb-2 flex items-center gap-2">
        <span className={`inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-semibold ${cfg.bgClass} ${cfg.colorClass}`}>
          <span className={`h-1.5 w-1.5 rounded-full ${cfg.dotClass}`} />
          {verdict}
        </span>
        {position && <span className="text-xs text-text-muted">Position #{position} in response</span>}
      </div>
      {competitors.length > 0 && (
        <p className="mb-2 text-xs text-text-muted">
          <span className="font-medium">Competing brands: </span>{competitors.join(', ')}
        </p>
      )}
      {action && (
        <p className="text-xs text-text-muted">
          <span className="font-medium">Action: </span>{action}
        </p>
      )}
    </div>
  )
}

function highlightBrand(text: string, brandName: string): React.ReactNode[] {
  if (!brandName) return [text]
  const parts = text.split(new RegExp(`(${brandName})`, 'gi'))
  return parts.map((part, i) =>
    part.toLowerCase() === brandName.toLowerCase()
      ? <mark key={i} className="not-italic rounded px-0.5 bg-brand-accent/10 text-brand-accent">{part}</mark>
      : part
  )
}

function ResultRow({ result, brandName }: { result: SimResult; brandName: string }) {
  const [expanded, setExpanded] = React.useState(false)
  return (
    <>
      <tr className="border-b border-border transition-colors hover:bg-surface-overlay">
        <td className="max-w-xs truncate px-4 py-3 text-sm text-text-muted">{result.prompt}</td>
        <td className="px-4 py-3">
          <span className="rounded border border-border bg-surface-raised px-2 py-1 text-xs font-medium text-text-muted">
            {result.platform}
          </span>
        </td>
        <td className="px-4 py-3"><MentionedBadge mentioned={result.mentioned} /></td>
        <td className="px-4 py-3 tabular-nums text-sm text-text-muted">
          {result.position !== null ? `#${result.position}` : '—'}
        </td>
        <td className="px-4 py-3"><SentimentChip sentiment={result.sentiment} /></td>
        <td className="px-4 py-3">
          {result.error ? (
            <span className="flex items-center gap-1 text-xs text-feedback-warning">
              <AlertTriangle className="h-3 w-3" /> Error
            </span>
          ) : (
            <button
              onClick={() => setExpanded(!expanded)}
              className="text-text-muted transition-colors hover:text-brand-accent"
            >
              {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
            </button>
          )}
        </td>
      </tr>
      {expanded && !result.error && (
        <tr className="bg-surface-raised">
          <td colSpan={6} className="px-6 py-4">
            <div className="rounded-lg border border-border bg-surface p-4">
              <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-text-muted">
                {result.platform} Response
              </p>
              <p className="whitespace-pre-wrap text-sm leading-relaxed text-text-muted">
                {highlightBrand(result.fullResponse, brandName)}
              </p>
              {result.visibilityVerdict && (
                <VisibilityVerdictCard
                  verdict={result.visibilityVerdict}
                  position={result.position}
                  competitors={result.competitorMentions}
                  action={result.improvementAction ?? ''}
                />
              )}
            </div>
          </td>
        </tr>
      )}
      {expanded && result.error && (
        <tr className="bg-surface-raised">
          <td colSpan={6} className="px-6 py-4">
            <div className="rounded-lg border border-feedback-error/30 bg-feedback-error/10 p-4">
              <p className="mb-1 text-xs font-semibold text-feedback-error">Query Error</p>
              <p className="text-sm text-feedback-error">{result.error}</p>
            </div>
          </td>
        </tr>
      )}
    </>
  )
}

function HistoryTab({ brandName }: { brandName: string }) {
  const [expandedId, setExpandedId] = React.useState<string | null>(null)

  const { data, isLoading } = useQuery({
    queryKey: ['simulate-history'],
    queryFn: () => api.get<{ results?: HistoryResult[] }>('/api/simulate/history'),
  })

  const items = data?.results ?? []

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Spinner />
      </div>
    )
  }

  if (items.length === 0) {
    return (
      <div className="rounded-xl border border-dashed border-border bg-surface-raised/30 p-12 text-center">
        <History className="mx-auto mb-4 h-10 w-10 text-text-muted opacity-40" />
        <p className="mb-1 font-medium text-text">No simulations yet</p>
        <p className="text-sm text-text-muted">Run your first simulation to see results here</p>
      </div>
    )
  }

  return (
    <div className="space-y-3">
      {items.map((item) => {
        const isExpanded = expandedId === item.id
        const displayPlatform =
          Object.entries(PLATFORM_MAP).find(([, v]) => v === item.platform)?.[0] ?? item.platform
        return (
          <div key={item.id} className="overflow-hidden rounded-xl border border-border bg-surface">
            <div className="flex items-start justify-between gap-4 px-5 py-4">
              <div className="min-w-0 flex-1">
                <p className="mb-2 line-clamp-2 text-sm font-medium leading-snug text-text">
                  {item.query_text}
                </p>
                <div className="flex flex-wrap items-center gap-2">
                  <span className="rounded border border-border bg-surface-raised px-2 py-0.5 text-xs font-medium text-text-muted">
                    {displayPlatform}
                  </span>
                  {item.brand_mentioned ? (
                    <span className="flex items-center gap-1 text-xs font-semibold text-feedback-success">
                      <CheckCircle2 className="h-3.5 w-3.5" /> Mentioned
                    </span>
                  ) : (
                    <span className="flex items-center gap-1 text-xs font-semibold text-brand-accent">
                      <XCircle className="h-3.5 w-3.5" /> Not mentioned
                    </span>
                  )}
                  {item.brand_mentioned && item.sentiment && item.sentiment !== 'not_mentioned' && (
                    <span className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold capitalize ${SENTIMENT_COLORS[item.sentiment] ?? ''}`}>
                      {item.sentiment}
                    </span>
                  )}
                  {item.competitor_mentions.length > 0 && (
                    <span className="text-[10px] text-text-muted opacity-60">
                      Competitors: {item.competitor_mentions.slice(0, 3).join(', ')}
                      {item.competitor_mentions.length > 3 ? ` +${item.competitor_mentions.length - 3}` : ''}
                    </span>
                  )}
                </div>
              </div>
              <div className="flex shrink-0 items-center gap-3">
                <span className="flex items-center gap-1 text-xs text-text-muted opacity-60">
                  <Clock className="h-3 w-3" />
                  {new Date(item.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}{' '}
                  {new Date(item.created_at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                </span>
                <button
                  onClick={() => setExpandedId(isExpanded ? null : item.id)}
                  className="text-text-muted transition-colors hover:text-brand-accent"
                >
                  {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                </button>
              </div>
            </div>
            {isExpanded && (
              <div className="border-t border-border bg-surface-raised px-5 py-4">
                <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-text-muted">
                  {displayPlatform} Response
                </p>
                <p className="whitespace-pre-wrap text-sm leading-relaxed text-text-muted">
                  {highlightBrand(item.response_text, brandName)}
                </p>
              </div>
            )}
          </div>
        )
      })}
      <p className="pt-2 text-center text-xs text-text-muted opacity-60">
        Showing your 20 most recent simulations
      </p>
    </div>
  )
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export function SimulatorPage() {
  const queryClient = useQueryClient()

  const { data: creditsData } = useQuery({
    queryKey: ['credits-balance'],
    queryFn: () => api.get<{ balance: number }>('/api/credits/balance'),
  })

  const { website: activeWebsite, websiteId } = useActiveWebsite()

  const { data: customPromptsData } = useQuery({
    queryKey: ['simulate-custom-prompts'],
    queryFn: () => api.get<{ used?: number; limit?: number; isGrowthPlus?: boolean; canUse?: boolean }>(
      '/api/simulate/custom-prompts'
    ),
  })

  const creditBalance = creditsData?.balance ?? null
  const brandName = activeWebsite?.name ?? ''
  const customPromptStatus = {
    used: customPromptsData?.used ?? 0,
    limit: customPromptsData?.limit ?? 3,
    isGrowthPlus: customPromptsData?.isGrowthPlus ?? false,
    canUse: customPromptsData?.canUse ?? true,
  }

  const [activeTab, setActiveTab] = React.useState<'simulate' | 'history'>('simulate')
  const [search, setSearch] = React.useState('')
  const [industryFilter, setIndustryFilter] = React.useState('All')
  const [selectedPrompt, setSelectedPrompt] = React.useState<Prompt | null>(PROMPTS[0] ?? null)
  const [selectedPlatforms, setSelectedPlatforms] = React.useState<string[]>(['Groq (Llama 3.3)'])
  const [running, setRunning] = React.useState(false)
  const [results, setResults] = React.useState<SimResult[]>([])
  const [hasRun, setHasRun] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)
  const [customText, setCustomText] = React.useState('')
  const [wasRewritten, setWasRewritten] = React.useState(false)
  const [rewrittenPrompt, setRewrittenPrompt] = React.useState('')
  const [originalPrompt, setOriginalPrompt] = React.useState('')

  const isUsingCustom = customText.trim() !== ''
  const activePromptText = isUsingCustom ? customText.trim() : (selectedPrompt?.text ?? '')

  const filteredPrompts = PROMPTS.filter((p) => {
    const matchSearch = p.text.toLowerCase().includes(search.toLowerCase())
    const matchIndustry = industryFilter === 'All' || p.industry === industryFilter
    return matchSearch && matchIndustry
  })

  const promptCountByIndustry = (industry: string) =>
    industry === 'All' ? PROMPTS.length : PROMPTS.filter((p) => p.industry === industry).length

  const creditCost = sumPlatformQueryCredits(
    selectedPlatforms.map((p) => PLATFORM_MAP[p] ?? p.toLowerCase())
  )

  const togglePlatform = (p: string) => {
    setSelectedPlatforms((prev) =>
      prev.includes(p) ? prev.filter((x) => x !== p) : [...prev, p]
    )
  }

  const canRunDisabled =
    !activePromptText ||
    selectedPlatforms.length === 0 ||
    running ||
    (creditBalance !== null && creditBalance < creditCost) ||
    (isUsingCustom && !customPromptStatus.canUse)

  const runSimulation = async () => {
    if (!activePromptText || selectedPlatforms.length === 0) return
    setRunning(true)
    setError(null)
    setWasRewritten(false)
    setRewrittenPrompt('')
    setOriginalPrompt('')

    try {
      const apiPlatforms = selectedPlatforms.map((p) => PLATFORM_MAP[p] ?? p.toLowerCase())
      const data = await api.post<SimulateResponse>('/api/simulate/sync', {
        prompt_text: activePromptText,
        platforms: apiPlatforms,
        website_id: websiteId ?? undefined,
        ...(isUsingCustom ? { is_custom: true } : {}),
      })

      if (typeof data.new_balance === 'number') {
        queryClient.setQueryData<{ balance: number }>(['credits-balance'], { balance: data.new_balance })
      }

      if (data.was_rewritten && data.original_prompt) {
        setWasRewritten(true)
        setOriginalPrompt(data.original_prompt)
        setRewrittenPrompt(data.rewritten_prompt ?? activePromptText)
      }

      if (isUsingCustom) {
        void queryClient.invalidateQueries({ queryKey: ['simulate-custom-prompts'] })
      }

      const simResults: SimResult[] = (data.results ?? []).map((r, i) => {
        const displayPlatform =
          Object.entries(PLATFORM_MAP).find(([, v]) => v === r.platform)?.[0] ?? r.platform
        return {
          id: `result-${i}-${Date.now()}`,
          prompt: activePromptText,
          platform: displayPlatform,
          mentioned: r.brand_mentioned,
          position: r.mention_position,
          sentiment: r.brand_mentioned ? (r.sentiment as Sentiment) : null,
          fullResponse: r.raw_response,
          competitorMentions: r.competitor_mentions ?? [],
          ...(r.visibility_verdict !== undefined ? { visibilityVerdict: r.visibility_verdict } : {}),
          ...(r.improvement_action !== undefined ? { improvementAction: r.improvement_action } : {}),
          ...(r.error !== undefined ? { error: r.error } : {}),
        }
      })

      setResults(simResults)
      setHasRun(true)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Network error — please check your connection and try again.')
    } finally {
      setRunning(false)
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-text">LLM Simulator</h1>
          <p className="mt-1 text-sm text-text-muted">
            Test how AI models respond to industry queries and track your brand mentions
          </p>
        </div>
        {creditBalance !== null && (
          <div className="flex items-center gap-1.5 rounded-full border border-feedback-success/30 bg-feedback-success/10 px-3 py-1.5 text-sm font-semibold text-feedback-success">
            <Zap className="h-3.5 w-3.5" />
            {creditBalance.toLocaleString()} credits
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="flex w-fit gap-1 rounded-lg border border-border bg-surface-raised p-1">
        {(['simulate', 'history'] as const).map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`flex items-center gap-2 rounded-md px-4 py-2 text-sm font-semibold transition-colors ${
              activeTab === tab
                ? 'bg-surface text-text shadow-sm'
                : 'text-text-muted hover:text-text'
            }`}
          >
            {tab === 'simulate' ? <Play className="h-3.5 w-3.5" /> : <History className="h-3.5 w-3.5" />}
            {tab === 'simulate' ? 'Simulate' : 'History'}
          </button>
        ))}
      </div>

      {activeTab === 'history' && <HistoryTab brandName={brandName} />}

      {activeTab === 'simulate' && (
        <>
          {/* Error banner */}
          {error && (
            <div className="flex items-center gap-2 rounded-lg border border-feedback-error/30 bg-feedback-error/10 px-4 py-3 text-sm text-feedback-error">
              <AlertTriangle className="h-4 w-4 shrink-0" />
              {error}
            </div>
          )}

          {/* No website notice */}
          {!websiteId && (
            <div className="rounded-lg border border-feedback-warning/30 bg-feedback-warning/10 px-4 py-3 text-sm text-feedback-warning">
              No website found — simulations will run but brand detection is skipped.{' '}
              <a href="/dashboard/settings" className="font-medium underline hover:opacity-80">
                Add your website in Settings
              </a>{' '}
              to enable brand mention tracking.
            </div>
          )}

          {/* Main panel */}
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            {/* Left: Prompt library */}
            <div className="flex flex-col overflow-hidden rounded-xl border border-border bg-surface">
              <div className="border-b border-border px-5 py-4">
                <h2 className="mb-3 text-sm font-semibold text-text">Prompt Library</h2>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-text-muted" />
                  <input
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search prompts across all industries..."
                    className="w-full rounded-lg border border-border bg-surface py-2 pl-9 pr-3 text-sm text-text placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-brand-accent"
                  />
                </div>
              </div>

              <div className="flex overflow-hidden" style={{ maxHeight: 420 }}>
                {/* Industry sidebar */}
                <div className="w-36 shrink-0 overflow-y-auto border-r border-border bg-surface-raised/40">
                  {INDUSTRIES.map((ind) => (
                    <button
                      key={ind}
                      onClick={() => setIndustryFilter(ind)}
                      className={`w-full border-l-2 px-3 py-2.5 text-left text-xs transition-colors ${
                        industryFilter === ind
                          ? 'border-brand-accent bg-brand-accent/10 font-semibold text-text'
                          : 'border-transparent text-text-muted hover:bg-surface-overlay'
                      }`}
                    >
                      <span className="block leading-snug">{ind}</span>
                      <span className={`text-[10px] ${industryFilter === ind ? 'text-brand-accent' : 'text-text-muted opacity-60'}`}>
                        {promptCountByIndustry(ind)}
                      </span>
                    </button>
                  ))}
                </div>

                {/* Prompt list */}
                <div className="flex-1 divide-y divide-border overflow-y-auto">
                  {filteredPrompts.length === 0 ? (
                    <div className="px-5 py-8 text-center text-sm text-text-muted">
                      No prompts match your search
                    </div>
                  ) : (
                    filteredPrompts.map((p) => {
                      const isSelected = selectedPrompt?.id === p.id && !isUsingCustom
                      return (
                        <button
                          key={p.id}
                          onClick={() => { setSelectedPrompt(p); setCustomText('') }}
                          className={`w-full px-4 py-3.5 text-left transition-colors hover:bg-surface-overlay ${
                            isSelected ? 'border-l-2 border-brand-accent bg-brand-accent/10' : ''
                          }`}
                        >
                          <p className={`mb-1.5 text-sm leading-snug ${isSelected ? 'text-brand-accent' : 'text-text-muted'}`}>
                            {p.text}
                          </p>
                          <div className="flex items-center gap-2">
                            <span className="rounded border border-border bg-surface-raised px-1.5 py-0.5 text-[10px] font-semibold text-text-muted capitalize">
                              {QUERY_TYPE_LABELS[p.queryType]}
                            </span>
                            <span className="text-[10px] text-text-muted opacity-60">{p.industry}</span>
                          </div>
                        </button>
                      )
                    })
                  )}
                </div>
              </div>
            </div>

            {/* Right: Simulation controls */}
            <div className="flex flex-col gap-5 rounded-xl border border-border bg-surface p-5">
              <h2 className="text-sm font-semibold text-text">Simulation Controls</h2>

              {/* Selected prompt display */}
              <div>
                <label className="mb-2 block text-xs font-semibold uppercase tracking-wider text-text-muted">
                  Selected Prompt
                </label>
                <div className="rounded-lg border border-border bg-surface-raised p-3">
                  <p className="text-sm text-text">
                    {isUsingCustom ? customText : (selectedPrompt?.text ?? 'No prompt selected')}
                  </p>
                </div>
              </div>

              {/* Platform selection */}
              <div>
                <label className="mb-2 block text-xs font-semibold uppercase tracking-wider text-text-muted">
                  Platforms
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {PLATFORMS.map((p) => (
                    <label key={p} className="group flex cursor-pointer items-center gap-2.5">
                      <input
                        type="checkbox"
                        checked={selectedPlatforms.includes(p)}
                        onChange={() => togglePlatform(p)}
                        className="h-4 w-4 cursor-pointer rounded border-border accent-brand-accent"
                      />
                      <span className="text-sm text-text-muted transition-colors group-hover:text-text">{p}</span>
                      {p === 'ChatGPT' && (
                        <span className="text-[10px] text-text-muted opacity-60">GPT-4o mini</span>
                      )}
                      {p === 'Bing Copilot' && (
                        <span className="text-[10px] text-text-muted opacity-60">(soon)</span>
                      )}
                      {p === 'Groq (Llama 3.3)' && (
                        <span className="text-[10px] font-semibold text-feedback-success">free</span>
                      )}
                    </label>
                  ))}
                </div>
              </div>

              {/* Credit cost */}
              <div className="rounded-lg border border-feedback-warning/20 bg-feedback-warning/10 px-4 py-3">
                <div className="flex items-center gap-2">
                  <Zap className="h-4 w-4 text-feedback-warning" />
                  <span className="text-sm text-feedback-warning">
                    This will cost <span className="font-bold">{creditCost} credit{creditCost !== 1 ? 's' : ''}</span>
                  </span>
                </div>
                {creditBalance !== null && creditBalance < creditCost && (
                  <p className="mt-1.5 text-xs text-brand-accent">
                    Insufficient credits.{' '}
                    <a href="/dashboard/settings" className="underline">Top up here.</a>
                  </p>
                )}
              </div>

              {/* Custom prompt */}
              <div>
                <div className="mb-2 flex items-center justify-between">
                  <label className="text-xs font-semibold uppercase tracking-wider text-text-muted">
                    Custom Prompt
                  </label>
                  {!customPromptStatus.isGrowthPlus && (
                    <span className="text-[10px] text-text-muted opacity-60">
                      {customPromptStatus.used} of {customPromptStatus.limit} used this month
                    </span>
                  )}
                </div>
                {!customPromptStatus.isGrowthPlus && !customPromptStatus.canUse ? (
                  <div>
                    <textarea
                      disabled
                      placeholder="You've used all 3 free custom prompts this month..."
                      className="w-full cursor-not-allowed resize-none rounded-lg border border-border bg-surface-raised px-3 py-2 text-sm text-text-muted placeholder:text-text-muted focus:outline-none"
                      rows={3}
                    />
                    <p className="mt-1.5 text-xs text-brand-accent">
                      You&apos;ve used all 3 free custom prompts this month.{' '}
                      <a href="/pricing" className="underline hover:opacity-80">Upgrade to Growth</a> for unlimited.
                    </p>
                  </div>
                ) : (
                  <textarea
                    value={customText}
                    onChange={(e) => setCustomText(e.target.value)}
                    placeholder="Enter your own prompt... (overrides selected prompt)"
                    className="w-full resize-none rounded-lg border border-border bg-surface px-3 py-2 text-sm text-text placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-brand-accent"
                    rows={3}
                  />
                )}
              </div>

              {/* Run button */}
              <Button
                className="mt-auto w-full py-3"
                onClick={() => void runSimulation()}
                disabled={canRunDisabled}
              >
                {running ? (
                  <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Querying LLMs…</>
                ) : (
                  <><Play className="mr-2 h-4 w-4" /> Run Simulation</>
                )}
              </Button>
            </div>
          </div>

          {/* Prompt rewrite notice */}
          {wasRewritten && (
            <div className="flex items-start gap-2 rounded-xl border border-feedback-warning/30 bg-feedback-warning/10 px-4 py-3 text-sm">
              <span>✏️</span>
              <div>
                <span className="font-medium text-feedback-warning">Prompt adjusted for business context:</span>
                <span className="ml-1 text-feedback-warning">&ldquo;{rewrittenPrompt}&rdquo;</span>
                <p className="mt-0.5 text-xs text-feedback-warning opacity-70">
                  Original: &ldquo;{originalPrompt}&rdquo;
                </p>
              </div>
            </div>
          )}

          {/* Results table */}
          {(hasRun || results.length > 0) && (
            <div className="overflow-hidden rounded-xl border border-border bg-surface">
              <div className="border-b border-border px-6 py-4">
                <h2 className="text-sm font-semibold text-text">Simulation Results</h2>
                <p className="mt-0.5 text-xs text-text-muted">
                  {results.length} result{results.length !== 1 ? 's' : ''} — click expand to see the full AI response
                </p>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border bg-surface-raised">
                      {['Prompt', 'Platform', 'Mentioned?', 'Position', 'Sentiment', ''].map((h, i) => (
                        <th key={i} className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-text-muted">
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {results.map((r) => (
                      <ResultRow key={r.id} result={r} brandName={brandName} />
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Empty state */}
          {!hasRun && (
            <div className="rounded-xl border border-dashed border-border bg-surface-raised/30 p-12 text-center">
              <Play className="mx-auto mb-4 h-10 w-10 text-text-muted opacity-40" />
              <p className="mb-1 font-medium text-text">No results yet</p>
              <p className="text-sm text-text-muted">
                Select a prompt and platforms above, then run the simulation
              </p>
              <p className="mt-2 text-xs text-text-muted opacity-60">
                Results are returned live from the real AI APIs
              </p>
            </div>
          )}
        </>
      )}
    </div>
  )
}
